import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

const PEEK_SLIDE_FRACTION = 2 / 3;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The mobile slideshow component.
 * @property {import('./slideshow').Slideshow} [slideshowDesktop] - The desktop dual-hero slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {ResizeObserver | undefined} */
  #peekResizeObserver;

  /** @type {AbortController | undefined} */
  #peekController;

  /** @type {number} */
  #lastDesktopSlideWidth = 0;

  /** @type {boolean} */
  #loopResetting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initDesktopPeekCarousel();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#peekResizeObserver?.disconnect();
    this.#peekController?.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    const desktopSlideshow = this.refs.slideshowDesktop;
    const mobileSlideshow = this.refs.slideshow;

    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 750px)').matches) {
      return desktopSlideshow ?? mobileSlideshow;
    }

    return mobileSlideshow ?? desktopSlideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   * @returns {{ cloneIndex: number, realLastIndex: number }}
   */
  #getDesktopLoopBounds(slideshow) {
    const slides = slideshow.slides ?? [];
    const cloneIndex = slides.findIndex((slide) => slide.dataset.loopClone === 'start');

    return {
      cloneIndex,
      realLastIndex: cloneIndex > 0 ? cloneIndex - 1 : slides.length - 1,
    };
  }

  /**
   * @param {HTMLElement} scroller
   * @returns {number}
   */
  #getDesktopSlideWidth(scroller) {
    const cssWidth = parseFloat(getComputedStyle(scroller).getPropertyValue('--pdp-hero-slide-width'));
    if (cssWidth > 0) return cssWidth;

    return Math.round(scroller.clientWidth * PEEK_SLIDE_FRACTION);
  }

  #initDesktopPeekCarousel() {
    if (this.presentation !== 'carousel') return;

    const slideshow = this.refs.slideshowDesktop;
    const scroller = slideshow?.refs.scroller;

    if (!(slideshow instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    this.#peekController?.abort();
    this.#peekController = new AbortController();
    const { signal } = this.#peekController;

    this.#installDesktopLoopHandlers(slideshow, scroller, signal);

    const layout = () => {
      if (!window.matchMedia('(min-width: 750px)').matches) return;

      const slides = slideshow.slides;
      if (!slides?.length) return;

      const slideWidth = Math.round(scroller.clientWidth * PEEK_SLIDE_FRACTION);
      const slideWidthValue = `${slideWidth}px`;
      const widthChanged = slideWidth !== this.#lastDesktopSlideWidth;

      scroller.style.setProperty('--pdp-hero-slide-width', slideWidthValue);

      for (const slide of slides) {
        slide.style.setProperty('--slide-width', slideWidthValue);
        slide.style.width = slideWidthValue;
        slide.style.flexBasis = slideWidthValue;
        slide.style.flexShrink = '0';
      }

      if (widthChanged) {
        this.#lastDesktopSlideWidth = slideWidth;
        const { cloneIndex } = this.#getDesktopLoopBounds(slideshow);
        const current = slideshow.current ?? 0;
        const safeIndex = cloneIndex >= 0 && current === cloneIndex ? 0 : current;

        scroller.scrollTo({
          left: safeIndex * slideWidth,
          behavior: 'auto',
        });
      }

      this.#updateDesktopPeekVisibility(slideshow);
    };

    layout();

    this.#peekResizeObserver?.disconnect();
    this.#peekResizeObserver = new ResizeObserver(layout);
    this.#peekResizeObserver.observe(scroller);

    slideshow.addEventListener(
      SlideshowSelectEvent.eventName,
      () => {
        this.#updateDesktopPeekVisibility(slideshow);
      },
      { signal }
    );

    const onScrollSettled = () => {
      if (this.#loopResetting) return;
      this.#maybeResetDesktopLoop(slideshow, scroller);
    };

    if ('onscrollend' in scroller) {
      scroller.addEventListener('scrollend', onScrollSettled, { signal });
    } else {
      let scrollTimer;
      scroller.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(onScrollSettled, 120);
        },
        { signal }
      );
    }
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   * @param {HTMLElement} scroller
   * @param {AbortSignal} signal
   */
  #installDesktopLoopHandlers(slideshow, scroller, signal) {
    const { cloneIndex, realLastIndex } = this.#getDesktopLoopBounds(slideshow);
    if (cloneIndex < 0) return;

    const originalNext = slideshow.next.bind(slideshow);
    const originalPrevious = slideshow.previous.bind(slideshow);

    slideshow.next = (event, options) => {
      if (this.#loopResetting) return;

      if (slideshow.current === cloneIndex) {
        this.#resetDesktopLoopToStart(slideshow, scroller);
        return;
      }

      if (slideshow.current === realLastIndex) {
        slideshow.select(cloneIndex, event, options);
        return;
      }

      originalNext(event, options);
    };

    slideshow.previous = (event, options) => {
      if (this.#loopResetting) return;

      if (slideshow.current === 0 && slideshow.infinite) {
        slideshow.select(realLastIndex, event, options);
        return;
      }

      originalPrevious(event, options);
    };

    signal.addEventListener('abort', () => {
      slideshow.next = originalNext;
      slideshow.previous = originalPrevious;
    });
  }

  /**
   * When the scroller stops on the cloned first slide, silently snap back to index 0.
   * Same image is on screen, so the reposition is invisible.
   * @param {import('./slideshow').Slideshow} slideshow
   * @param {HTMLElement} scroller
   */
  #maybeResetDesktopLoop(slideshow, scroller) {
    const { cloneIndex } = this.#getDesktopLoopBounds(slideshow);
    if (cloneIndex < 0) return;

    const slideWidth = this.#getDesktopSlideWidth(scroller);
    if (!slideWidth) return;

    const settledIndex = Math.round(scroller.scrollLeft / slideWidth);
    if (settledIndex !== cloneIndex) return;

    this.#resetDesktopLoopToStart(slideshow, scroller);
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   * @param {HTMLElement} scroller
   */
  #resetDesktopLoopToStart(slideshow, scroller) {
    if (this.#loopResetting) return;

    this.#loopResetting = true;

    if (typeof slideshow.snapLoopReset === 'function') {
      slideshow.snapLoopReset(0, 0);
    } else {
      scroller.scrollLeft = 0;
      slideshow.current = 0;
    }

    slideshow.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== 0}`);
    });

    this.#updateDesktopPeekVisibility(slideshow);

    requestAnimationFrame(() => {
      this.#loopResetting = false;
    });
  }

  /**
   * @param {import('./slideshow').Slideshow} slideshow
   */
  #updateDesktopPeekVisibility(slideshow) {
    const slides = slideshow.slides;
    if (!slides?.length) return;

    const current = slideshow.current;
    const { cloneIndex, realLastIndex } = this.#getDesktopLoopBounds(slideshow);

    let peekIndex = current + 1;

    if (current >= realLastIndex && cloneIndex >= 0) {
      peekIndex = cloneIndex;
    } else if (peekIndex > realLastIndex) {
      peekIndex = -1;
    }

    slides.forEach((slide, index) => {
      const isPeek =
        index === peekIndex ||
        (index === cloneIndex + 1 && slide.dataset.loopClone === 'buffer' && current === cloneIndex);
      slide.classList.toggle('pdp-hero-peek-visible', isPeek);
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
